import { Divider, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { AuthoringTailoredValue } from "../amends/components/authoring-tailored-value"
import { PreviewText } from "./preview-text"
import { RichTextComparison } from "./rich-text-comparison"
import { buildAuthoringRecommendationComparison } from "./lib/authoring-recommendation-comparison-presenters"

const FULL_WIDTH_CHIP_SX = {
    width: "100%",
    justifyContent: "flex-start",
    "& .MuiChip-label": {
        width: "100%",
    },
}

export function AuthoringRecommendationComparison({ item }) {
    const comparison = buildAuthoringRecommendationComparison(item)
    const auditKeys = comparison?.auditKeys ?? []
    const displayAuditKeys = auditKeys.filter((key) => key !== "unitOfMeasure")
    const plainComparison = comparison?.plainComparison ?? null
    const contentComparison = comparison?.contentComparison ?? null
    const noteComparison = comparison?.noteComparison ?? null

    if (!comparison) return null

    return (
        <Stack spacing={1} sx={{ mt: 0.5 }}>
            <If
                truthy={shouldShowAuditSummary({
                    auditKeys: displayAuditKeys,
                    plainComparison,
                    contentComparison,
                    noteComparison,
                })}
            >
                <Stack spacing={0.5}>
                    <Repeat
                        list={displayAuditKeys}
                        item={
                            <AuthoringTailoredValue
                                original={comparison?.original}
                                tailored={comparison?.tailored}
                                chipSx={FULL_WIDTH_CHIP_SX}
                            />
                        }
                    />
                </Stack>
            </If>
            <If truthy={Boolean(plainComparison)}>
                <Stack spacing={0.75}>
                    <Typography variant="subtitle2">{plainComparison?.label}</Typography>
                    <If truthy={plainComparison?.isNew}>
                        <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                New
                            </Typography>
                            <PreviewText
                                value={plainComparison?.tailoredValue}
                                dataCy="authoring-recommendation-comparison-new"
                                originalValue={plainComparison?.tailoredValue}
                                comparisonValue={plainComparison?.tailoredValue}
                            />
                        </Stack>
                    </If>
                    <If truthy={!plainComparison?.isNew}>
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: "stretch" }}>
                            <Stack spacing={0.75} sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    New
                                </Typography>
                                <PreviewText
                                    value={plainComparison?.tailoredValue}
                                    dataCy="authoring-recommendation-comparison-new"
                                    originalValue={plainComparison?.originalValue}
                                    comparisonValue={plainComparison?.originalValue}
                                />
                            </Stack>
                            <Divider orientation="vertical" flexItem />
                            <Stack spacing={0.75} sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Original
                                </Typography>
                                <PreviewText
                                    value={plainComparison?.originalValue}
                                    dataCy="authoring-recommendation-comparison-original"
                                    originalValue={plainComparison?.tailoredValue}
                                    comparisonValue={plainComparison?.tailoredValue}
                                    showRemoved
                                />
                            </Stack>
                        </Stack>
                    </If>
                </Stack>
            </If>
            <If truthy={Boolean(contentComparison)}>
                <RichTextComparison
                    label={contentComparison?.label}
                    originalValue={contentComparison?.originalValue}
                    tailoredValue={contentComparison?.tailoredValue}
                    isNew={contentComparison?.isNew}
                />
            </If>
            <If truthy={Boolean(noteComparison)}>
                <RichTextComparison
                    label={noteComparison?.label}
                    originalValue={noteComparison?.originalValue}
                    tailoredValue={noteComparison?.tailoredValue}
                    isNew={noteComparison?.isNew}
                />
            </If>
        </Stack>
    )
}

function shouldShowAuditSummary({ auditKeys, plainComparison, contentComparison, noteComparison }) {
    if (!auditKeys.length) return false
    if (plainComparison?.label === "Task Title" && auditKeys.length === 1 && auditKeys[0] === "title") return false
    if (contentComparison && auditKeys.length === 1 && auditKeys[0] === "content") return false
    if (noteComparison && auditKeys.length === 1 && auditKeys[0] === "note") return false
    return true
}
